'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'

import BottomNavigation from '@mui/material/BottomNavigation'
import BottomNavigationAction from '@mui/material/BottomNavigationAction'
import Paper from '@mui/material/Paper'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import DashboardRounded from '@mui/icons-material/DashboardRounded'
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded'
import WaterDropRounded from '@mui/icons-material/WaterDropRounded'
import PhoneRounded from '@mui/icons-material/PhoneRounded'
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded'
import ComputerRounded from '@mui/icons-material/ComputerRounded'

import type { MenuItem as GiaMenuItem, MenuResponse } from '@/types/gia'

import { getLocalizedUrl } from '@/utils/i18n'
import { menuApi } from '@/lib/api'
import { useAuth } from '@/contexts/auth-context'

type BottomNavItem = {
  code: string
  label: string
  href: string
  icon: React.ReactElement
}

const iconByCode: Record<string, React.ReactElement> = {
  dashboard: <DashboardRounded fontSize='small' />,
  personas: <PeopleAltRounded fontSize='small' />,
  diuresis: <WaterDropRounded fontSize='small' />,
  call_logs: <PhoneRounded fontSize='small' />,
  dietas: <ReceiptLongRounded fontSize='small' />,
  it: <ComputerRounded fontSize='small' />
}

const priorityCodes = ['dashboard', 'personas', 'diuresis', 'call_logs', 'dietas', 'it']

const normalizeKey = (value?: string) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]/g, '')

const flattenMenuItems = (response: MenuResponse): GiaMenuItem[] => {
  const sections = response?.sections ?? []
  return sections.flatMap(section => section.items ?? [])
}

const MobileBottomNav = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const pathname = usePathname()
  const params = useParams()
  const locale = typeof params.lang === 'string' ? params.lang : 'es'
  const { impersonatedCentroId, isAuthenticated } = useAuth()

  const [items, setItems] = useState<BottomNavItem[]>([])

  const activePath = useMemo(() => {
    const normalized = pathname.replace(`/${locale}`, '')
    return normalized || '/dashboard'
  }, [pathname, locale])

  useEffect(() => {
    let active = true

    const loadMenu = async () => {
      if (!isAuthenticated || !isMobile) return

      try {
        const response = await menuApi.getMenu(impersonatedCentroId)
        if (!active) return

        const flat = flattenMenuItems(response)
        const byCode = new Map<string, GiaMenuItem>()

        flat.forEach(item => {
          const codeKey = item.code || normalizeKey(item.url) || normalizeKey(item.name)
          if (codeKey) {
            byCode.set(codeKey, item)
          }
        })

        const nextItems = priorityCodes
          .map(code => {
            const item =
              byCode.get(code) ||
              byCode.get(normalizeKey(code)) ||
              flat.find(entry => normalizeKey(entry.url) === normalizeKey(`/${code}`))

            if (!item?.url) return null

            return {
              code,
              label: item.name,
              href: getLocalizedUrl(item.url, locale),
              icon: iconByCode[code] || <DashboardRounded fontSize='small' />
            }
          })
          .filter(Boolean) as BottomNavItem[]

        setItems(nextItems)
      } catch (error) {
        console.error('[MobileBottomNav] menu load error', error)
        if (!active) return

        setItems([
          {
            code: 'dashboard',
            label: 'Dashboard',
            href: getLocalizedUrl('/dashboard', locale),
            icon: iconByCode.dashboard
          }
        ])
      }
    }

    loadMenu()

    return () => {
      active = false
    }
  }, [impersonatedCentroId, isAuthenticated, isMobile, locale])

  if (!isMobile || items.length === 0) return null

  return (
    <Paper
      elevation={8}
      className='mui-fixed'
      sx={{
        left: 0,
        right: 0,
        bottom: 0,
        position: 'fixed',
        borderTop: '1px solid',
        borderColor: 'divider',
        zIndex: theme.zIndex.appBar,
        pb: 'env(safe-area-inset-bottom)'
      }}
    >
      <BottomNavigation
        showLabels
        value={activePath}
        sx={{
          height: 60,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 64
          }
        }}
      >
        {items.map(item => (
          <BottomNavigationAction
            key={item.code}
            label={item.label}
            icon={item.icon}
            value={item.href.replace(`/${locale}`, '')}
            component={Link}
            href={item.href}
          />
        ))}
      </BottomNavigation>
    </Paper>
  )
}

export default MobileBottomNav
